import { INIT_PAGINATION } from '@vindr/services/constants/common.constant';
import { SORT_ORDER } from '@vindr/services/constants/filter.constant';
import useControlModal from '@vindr/utils/hooks/useControlModal';
import { PaginationProps, SettingsContext } from '@vindr/services/interfaces';
import { useGetPatientsVisited } from '@vindr/services/actions/patient.action';
import { filterValidValue } from '@vindr/utils/helpers/common.helper';
import dayjs from 'dayjs';
import { Dispatch, createContext, useContext, useMemo, useState } from 'react';
import { ImmerHook, useImmer } from 'use-immer';

export const INIT_PATIENT_FILTER: Parameters<typeof useGetPatientsVisited>[0] = {
  sort_field: 'updated_at',
  sort_order: SORT_ORDER.DESC,
  updated_at_from: dayjs().startOf('days').toISOString(),
  updated_at_to: dayjs().endOf('days').toISOString(),
};

export interface PatientContext extends SettingsContext {
  patientList: ReturnType<typeof useGetPatientsVisited>['data']['data'];
  refetch: ReturnType<typeof useGetPatientsVisited>['refetch'];
  editItem: PatientContext['patientList'][number];
  filter: Parameters<typeof useGetPatientsVisited>[0];
  setFilter: ImmerHook<PatientContext['filter']>[1];
  setStateAction: Dispatch<React.SetStateAction<'create' | 'edit'>>;
}

const PatientContext = createContext({});
const PatientProvider = ({ children }: { children: React.ReactNode }) => {
  const [editItem, setEditItem] = useState<unknown>(null);
  const [stateAction, setStateAction] = useState<'create' | 'edit'>('create');
  const [filter, setFilter] =
    useImmer<Parameters<typeof useGetPatientsVisited>[0]>(INIT_PATIENT_FILTER);
  const [pagination, setPagination] = useImmer<PaginationProps>(INIT_PAGINATION);

  const { openModal, setCloseModal, setOpenAddModal, setOpenEditModal } = useControlModal(
    setEditItem,
    setStateAction
  );

  const { data, isFetching, refetch } = useGetPatientsVisited({
    limit: pagination.size,
    offset: (pagination.page - 1) * pagination.size,
    ...filterValidValue(filter),
  });

  const { count: total, data: patientList } = data || {};

  const memoizedValue = useMemo(
    () => ({
      editItem,
      filter,
      isLoading: isFetching,
      refetch,
      openModal,
      pagination,
      setCloseModal,
      setEditItem,
      setFilter,
      setOpenAddModal,
      setOpenEditModal,
      setPagination,
      stateAction,
      setStateAction,
      total,
      patientList,
    }),
    [
      editItem,
      filter,
      isFetching,
      refetch,
      openModal,
      pagination,
      setCloseModal,
      setEditItem,
      setFilter,
      setOpenAddModal,
      setOpenEditModal,
      setPagination,
      stateAction,
      setStateAction,
      total,
      patientList,
    ]
  );

  return <PatientContext.Provider value={memoizedValue}>{children}</PatientContext.Provider>;
};

PatientProvider.displayName = 'PatientProvider';

export const usePatientContext = () => {
  const context = useContext(PatientContext);

  if (!context) {
    throw new Error('usePatientContext must be used within a PatientProvider');
  }

  return context as PatientContext;
};

export default PatientProvider;
